import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtSigned, fmtUsd, fixed, WHY_SELL } from "@/lib/journal";
import { WhyPicker } from "@/components/WhyPicker";
import { CloudArrowDown, CircleNotch } from "@phosphor-icons/react";

export const SellDialog = ({ trade, onOpenChange, onSaved }) => {
  const { t } = useTranslation();
  const [exitHash, setExitHash] = useState("");
  const [exitPrice, setExitPrice] = useState("");
  const [sellVolume, setSellVolume] = useState("");
  const [notes, setNotes] = useState("");
  const [whySell, setWhySell] = useState("");
  const [exitDate, setExitDate] = useState(() => {
    const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  });
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (trade) {
      setExitHash(trade.exit_tx_hash || "");
      setExitPrice(trade.exit_price != null ? String(trade.exit_price) : "");
      setSellVolume(String(trade.volume));
      setNotes(trade.closing_notes || "");
      setWhySell(trade.why_sell || "");
    }
  }, [trade]);

  if (!trade) return null;

  const vol = parseFloat(sellVolume) || 0;
  const partial = vol > 0 && vol < trade.volume - 1e-12;
  const remaining = Math.max(0, trade.volume - vol);
  const pnl = exitPrice && vol ? vol * (parseFloat(exitPrice) - trade.entry_price) : null;
  const pnlPct = pnl != null ? (pnl / (vol * trade.entry_price)) * 100 : null;

  const fetchTx = async () => {
    if (!exitHash.trim()) return toast.error("Enter the exit transaction hash");
    setFetching(true);
    try {
      const { data } = await api.post("/tx/fetch", { tx_hash: exitHash.trim(), network: trade.network });
      let price = null;
      if (data.asset && data.volume && ["USDC", "USDT", "DAI"].includes(data.asset)) {
        price = data.volume / (vol || trade.volume);
      } else if (data.entry_price) {
        price = data.entry_price;
      }
      if (price) {
        setExitPrice(String(Number(fixed(price, 6, "0"))));
        toast.success("Exit price parsed from chain");
      } else {
        toast.warning(data.note || "Could not derive exit price — enter it manually");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch transaction");
    } finally {
      setFetching(false);
    }
  };

  const submit = async () => {
    if (!exitPrice) return toast.error("Exit price is required");
    if (!vol || vol <= 0) return toast.error("Sell volume must be greater than zero");
    if (vol > trade.volume + 1e-12) return toast.error("Sell volume exceeds the open position size");
    if (!whySell) return toast.error("Pick why you are selling — it powers the Human Factor stats");
    setSaving(true);
    try {
      await api.post(`/trades/${trade.id}/partial-exit`, {
        volume: vol,
        exit_price: parseFloat(exitPrice),
        exit_tx_hash: exitHash.trim() || null,
        closing_notes: notes || null,
        why_sell: whySell,
        exit_date: exitDate ? new Date(exitDate).toISOString() : null,
      });
      toast.success(partial ? `Sold ${vol} ${trade.asset}, ${remaining} still open` : "Position closed");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close trade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!trade} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="sell-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            {partial ? "Partial Exit" : "Close Position"} <span className="text-[#C58361]">/ Sell</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Sell all or part of the position — the remainder stays open at the same average cost.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="grid grid-cols-3 gap-3 border border-[#1F1F1F] bg-[#050505] p-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Asset</p>
              <p className="mt-1 font-mono text-sm text-white">{trade.asset}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Open Volume</p>
              <p className="mt-1 font-mono text-sm text-white">{trade.volume}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Avg Entry</p>
              <p className="mt-1 font-mono text-sm text-white">{fmtUsd(trade.entry_price, 4)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Exit Tx Hash</Label>
            <div className="flex gap-2">
              <Input
                data-testid="sell-txhash-input"
                value={exitHash}
                onChange={(e) => setExitHash(e.target.value)}
                placeholder="0x…"
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono text-xs focus-visible:ring-[#C58361]"
              />
              <Button
                data-testid="sell-fetch-btn"
                onClick={fetchTx}
                disabled={fetching}
                className="shrink-0 rounded-none bg-[#C58361] font-medium text-black hover:bg-[#A86B4C]"
              >
                {fetching ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowDown size={16} />}
                <span className="ml-2 hidden sm:inline">Fetch</span>
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("sell.exitDate")}</Label>
            <Input
              data-testid="sell-date-input"
              type="datetime-local"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono text-xs focus-visible:ring-[#C58361]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Sell Volume</Label>
              <Input
                data-testid="sell-volume-input"
                type="number" step="any" value={sellVolume}
                onChange={(e) => setSellVolume(e.target.value)}
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
              />
              <div className="flex gap-2">
                {[25, 50, 100].map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`sell-pct-${p}`}
                    onClick={() => setSellVolume(String(Number(fixed((Number(trade.volume) * p) / 100, 8, "0"))))}
                    className="border border-[#1F1F1F] px-2 py-0.5 text-[10px] text-zinc-500 transition-colors duration-200 hover:border-[#C58361] hover:text-[#C58361]"
                  >
                    {p}%
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Exit Price (USD)</Label>
              <Input
                data-testid="sell-price-input"
                type="number" step="any" value={exitPrice}
                onChange={(e) => setExitPrice(e.target.value)}
                placeholder="0.00"
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
              />
            </div>
          </div>

          <div className="border border-[#1F1F1F] bg-[#050505] p-4" data-testid="sell-pnl-preview">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Realized PnL on this exit</p>
            <p className={`mt-2 font-mono text-2xl ${pnl == null ? "text-zinc-600" : pnl >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
              {pnl == null ? "—" : `${fmtSigned(pnl)}  (${Number.isFinite(pnlPct) && pnlPct >= 0 ? "+" : ""}${fixed(pnlPct, 2)}%)`}
            </p>
            <p className="mt-2 font-mono text-[11px] text-zinc-500" data-testid="sell-remaining-hint">
              {partial
                ? `${remaining} ${trade.asset} stays open at ${fmtUsd(trade.entry_price, 4)} avg cost`
                : "Full exit — position will be closed"}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              Why Sell? <span className="text-[#C58361]">*</span>
            </Label>
            <WhyPicker options={WHY_SELL} value={whySell} onChange={setWhySell} testPrefix="sell-why" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Closing Thoughts / Summary</Label>
            <Textarea
              data-testid="sell-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="What worked, what didn't, lessons…"
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-[#C58361]"
            />
          </div>

          <Button
            data-testid="sell-submit-btn"
            onClick={submit}
            disabled={saving || !whySell}
            className="w-full rounded-none bg-[#C58361] py-6 font-medium text-black hover:bg-[#A86B4C]"
          >
            {saving ? "Saving…" : partial ? "Record Partial Exit" : "Close Position"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
